import { useApolloClient } from "@apollo/client";
import { Tracks } from "@fluidattacks/tracks";
import type { RefObject } from "react";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router";

import {
  hasValue,
  resolveOrganization,
  shouldQueryOrganizations,
  useOrgFromUrl,
  useUserOrganizations,
} from "./utils";

import { authContext } from "context/auth";
import type { GetGroupOrganizationQuery } from "gql/graphql";
import { GET_GROUP_ORGANIZATION } from "pages/dashboard/organization-redirect/queries";
import type { IEventEvidenceAttr } from "pages/event/evidence/types";
import type { IEvidenceItem } from "pages/finding/evidence/types";
import { getEnvironment } from "utils/environment";

interface IUseEvidenceAudit {
  addEvidenceAudit: (
    evidences: Record<string, IEvidenceItem | IEventEvidenceAttr>,
    fileName: string,
    instance: {
      id: string;
      name: "Event" | "Finding";
    },
  ) => void;
}

interface IUseAudit {
  addAuditEvent: (
    object: string,
    objectId: string,
    group?: string,
    metadata?: Record<string, string>,
  ) => void;
}

interface IUseObserverAudit {
  ref: RefObject<HTMLDivElement>;
}

const OBSERVER_DELAY = 1000;

/*
 * The schema types Query.group as non-null, but the owner resolution must
 * not depend on that contract holding at runtime: widen and check instead
 * of letting a TypeError fall through to the catch handler.
 */
const toOwnerOrganization = (
  group: GetGroupOrganizationQuery["group"] | null,
): string | undefined => (group === null ? undefined : group.organization);

const useAudit = (): IUseAudit => {
  const { userEmail } = useContext(authContext);
  const { groupName, organizationName } = useParams<{
    groupName: string;
    organizationName: string;
  }>();
  const orgFromUrl = useOrgFromUrl();
  const shouldQuery = shouldQueryOrganizations(organizationName, orgFromUrl);

  const { data: orgData } = useUserOrganizations(undefined, !shouldQuery);

  const resolvedOrgName = useMemo((): string => {
    if (hasValue(organizationName)) return organizationName ?? "";
    if (hasValue(orgFromUrl)) return orgFromUrl ?? "";

    return resolveOrganization(orgFromUrl, orgData?.me.organizations);
  }, [organizationName, orgFromUrl, orgData]);

  const client = useMemo((): Tracks => new Tracks(), []);
  const apolloClient = useApolloClient();

  const addAuditEvent = useCallback(
    (
      object: string,
      objectId: string,
      group?: string,
      metadata?: Record<string, string>,
    ): void => {
      if (
        getEnvironment() !== "production" &&
        process.env.FT_API_URL === undefined
      ) {
        return;
      }

      const eventGroup = group ?? groupName;

      const emitEvent = (eventOrganization: string | undefined): void => {
        client.event.create({
          action: "READ",
          author: userEmail || "unknown",
          date: new Date(),
          mechanism: "WEB",
          metadata: {
            ...metadata,
            ...(hasValue(eventGroup) ? { group: eventGroup } : {}),
            ...(hasValue(eventOrganization)
              ? { organization: eventOrganization }
              : {}),
            referer: document.referrer,
          },
          object,
          objectId,
        });
      };

      if (eventGroup === undefined || eventGroup === "") {
        emitEvent(organizationName ?? resolvedOrgName);

        return;
      }

      const urlOrganization = organizationName ?? orgFromUrl;
      if (hasValue(urlOrganization) && eventGroup === groupName) {
        emitEvent(urlOrganization);

        return;
      }

      /*
       * The recorded organization must be the one that owns the group;
       * guessing it from the actor context (localStorage, first org)
       * produced false group-company associations (issue #26417). Better
       * absent than wrong: omit it when the group cannot be resolved.
       */
      void apolloClient
        .query({
          fetchPolicy: "cache-first",
          query: GET_GROUP_ORGANIZATION,
          variables: { groupName: eventGroup },
        })
        .then(({ data }): void => {
          emitEvent(toOwnerOrganization(data.group));
        })
        .catch((): void => {
          emitEvent(undefined);
        });
    },
    [
      apolloClient,
      client,
      groupName,
      orgFromUrl,
      organizationName,
      resolvedOrgName,
      userEmail,
    ],
  );

  return { addAuditEvent };
};

const useEvidenceAudit = (): IUseEvidenceAudit => {
  const { addAuditEvent } = useAudit();
  const addEvidenceAudit = useCallback(
    (
      evidences: Record<string, IEvidenceItem | IEventEvidenceAttr>,
      fileName: string,
      instance: {
        id: string;
        name: "Event" | "Finding";
      },
    ): void => {
      const filteredEvidenceEntry = Object.entries(evidences).find(
        ([_, evidence]: [
          string,
          IEvidenceItem | IEventEvidenceAttr,
        ]): boolean => {
          if ("url" in evidence) {
            return evidence.url === fileName;
          }
          if ("fileName" in evidence) {
            return evidence.fileName === fileName;
          }

          return false;
        },
      );

      if (filteredEvidenceEntry) {
        const [key] = filteredEvidenceEntry;
        addAuditEvent(`${instance.name}.Evidences`, instance.id, undefined, {
          // eslint-disable-next-line camelcase -- tracks metadata keys are snake_case
          evidence_id: key.toLowerCase(),
        });
      }
    },
    [addAuditEvent],
  );

  return { addEvidenceAudit };
};

const useObserverAudit = (
  object: string,
  objectId: string,
): IUseObserverAudit => {
  const ref = useRef<HTMLDivElement>(null);
  const { addAuditEvent } = useAudit();

  useEffect((): (() => void) => {
    if (!ref.current) return (): void => undefined;
    const currentRef = ref.current;

    const observer = new IntersectionObserver(
      ([entry]): void => {
        if (entry.isIntersecting) addAuditEvent(object, objectId);
      },
      { threshold: 0.9 },
    );

    const timeoutId = setTimeout((): void => {
      observer.observe(currentRef);
    }, OBSERVER_DELAY);

    return (): void => {
      clearTimeout(timeoutId);
      observer.unobserve(currentRef);
    };
  }, [addAuditEvent, object, objectId]);

  return { ref };
};

export { useAudit, useEvidenceAudit, useObserverAudit };
